/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useCallback } from 'react';
import { View, FlatList, TextStyle, ViewStyle } from 'react-native';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import {
  getTextStyle,
  test_ID,
  getShape,
} from '@airasia-phoenix/react-native/utils/utils';
import { pd } from '../../utils';
import ListView, {
  ListViewProps,
} from '@airasia-phoenix/react-native/components/ListView/index';

export interface MenuViewProps {
  testID?: string;
  containerWidth?: number;
  listViewItems: ListViewProps[];
  style?: ViewStyle;
}

const MenuView = ({
  testID,
  containerWidth,
  listViewItems,
  style,
}: MenuViewProps) => {
  const testIDValue = testID ?? '';
  const getTestId = (name: string) => {
    return test_ID(testIDValue.length > 0 ? `${testIDValue}-${name}` : name);
  };

  // Subscribe to the theme so the container and row colours re-resolve on toggle.
  const { theme } = useUnistyles();

  const renderItem = useCallback(
    ({ item }: { item: ListViewProps }) => (
      <ListView
        primaryTextStyle={
          getTextStyle(
            pd.sys.typescale.sub_body_text,
            pd.sys.colour.content_primary,
          ) as TextStyle
        }
        {...item}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `theme` re-triggers pd.sys.* colour resolution, which eslint can't see
    [theme],
  );

  return (
    <View
      accessible={true}
      accessibilityLabel="Menu View"
      style={[
        getShape(pd.sys.shape.corner.large, containerWidth ?? 288),
        { width: containerWidth ?? 288 },
        styles.container,
        style,
      ]}
      {...getTestId('container')}
    >
      <FlatList
        data={listViewItems}
        keyExtractor={(item, index) => index.toString()}
        renderItem={renderItem}
        {...getTestId('list')}
      />
    </View>
  );
};

const styles = StyleSheet.create((pd) => ({
  container: {
    paddingVertical: pd.ref.space.spacing40.px,
    paddingHorizontal: pd.ref.space.spacing20.px,
    backgroundColor: pd.sys.colour.bg_primary.toString(),
    borderColor: pd.sys.colour.outline_low.toString(),
    borderWidth: 1,
    elevation: 2,
    overflow: 'hidden',
  },
}));

export default MenuView;
